import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import WysiwygEditor from '../../components/WysiwygEditor';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft,
  Trash2,
  Plus,
  Target,
  FileText,
  ClipboardList,
  CircleQuestionMark,
  Images
} from 'lucide-react';

// For static assets, use ASSET_URL in dev, hostname in prod
const ASSET_BASE_URL = process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_HOSTNAME || '';

// Sortable FAQ Item Component
function SortableFaqItem({ faq, index, onUpdate, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: faq.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg p-4 ${isDragging ? 'z-50' : ''}`}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Question {index + 1}
            </label>
            <input
              type="text"
              value={faq.question}
              onChange={(e) => onUpdate(faq.id, { question: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Enter question..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              Answer
            </label>
            <textarea
              value={faq.answer}
              onChange={(e) => onUpdate(faq.id, { answer: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={3}
              placeholder="Enter answer..."
            />
          </div>
        </div>
        <div className="flex items-center ml-4 space-x-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="p-2 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
            title="Drag to reorder"
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z"/>
            </svg>
          </button>
          <button
            type="button"
            onClick={() => onDelete(faq.id)}
            className="p-2 text-red-500 hover:text-red-700"
            title="Delete FAQ"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionEdit() {
  const navigate = useNavigate();
  const { campaignId, sectionId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState(null);
  const [sectionFaqs, setSectionFaqs] = useState([]);
  const [sectionGallery, setSectionGallery] = useState([]);
  const [uploadingBackground, setUploadingBackground] = useState(false);
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchSectionData();
  }, [fetchSectionData]);

  const fetchSectionData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch section details
      const sectionResponse = await api.get(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}`);
      setSection(sectionResponse.data.section);

      // Fetch section FAQs if it's a FAQ section
      if (sectionResponse.data.section.type === 'faqs') {
        const faqsResponse = await api.get(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs`);
        setSectionFaqs(faqsResponse.data.faqs || []);
      }

      // Fetch section gallery if it's a gallery section
      if (sectionResponse.data.section.type === 'gallery') {
        const galleryResponse = await api.get(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery`);
        setSectionGallery(galleryResponse.data.gallery || []);
      }
    } catch (error) {
      console.error('Failed to fetch section data:', error);
    } finally {
      setLoading(false);
    }
  }, [campaignId, sectionId]);

  const handleSectionUpdate = (field, value) => {
    setSection(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    
    setSaving(true);
    try {
      await api.put(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}`, {
        data: {
          background_image_url: section.background_image_url,
          full_width_background: section.full_width_background,
          about_header: section.about_header,
          about_description: section.about_description,
          about_with_form: section.about_with_form,
          faqs_header: section.faqs_header,
          faqs_description: section.faqs_description,
          sort_order: section.sort_order
        }
      });
      alert('Section saved successfully!');
    } catch (error) {
      console.error('Failed to save section:', error);
      alert('Failed to save section');
    } finally {
      setSaving(false);
    }
  };

  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const validVideoTypes = ['video/mp4', 'video/webm', 'video/ogg'];
    const validTypes = [...validImageTypes, ...validVideoTypes];
    
    if (!validTypes.includes(file.type)) {
      alert('Please upload an image (JPG, PNG, GIF) or video (MP4, WebM, OGG) file');
      return;
    }
    
    const maxSize = validVideoTypes.includes(file.type) ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File size must be less than ${validVideoTypes.includes(file.type) ? '50MB' : '10MB'}`);
      return;
    }

    setUploadingBackground(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/upload-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSection(prev => ({ ...prev, background_image_url: response.data.fileUrl }));
      e.target.value = '';
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload background: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingBackground(false);
    }
  };

  // FAQ Management
  const handleAddFaq = async () => {
    const newFaq = {
      question: '',
      answer: '',
      sort_order: sectionFaqs.length
    };

    try {
      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs`, newFaq);
      setSectionFaqs(prev => [...prev, response.data.faq]);
    } catch (error) {
      console.error('Failed to add FAQ:', error);
      alert('Failed to add FAQ');
    }
  };

  const handleUpdateFaq = (faqId, data) => {
    setSectionFaqs(prev => prev.map(faq => 
      faq.id === faqId ? { ...faq, ...data } : faq
    ));
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    
    try {
      await api.delete(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs/${faqId}`);
      setSectionFaqs(prev => prev.filter(faq => faq.id !== faqId));
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      alert('Failed to delete FAQ');
    }
  };

  const handleReorderFaqs = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sectionFaqs.findIndex(faq => faq.id === active.id);
    const newIndex = sectionFaqs.findIndex(faq => faq.id === over.id);
    
    const reorderedFaqs = arrayMove(sectionFaqs, oldIndex, newIndex);
    setSectionFaqs(reorderedFaqs);

    // Update sort orders
    const updates = reorderedFaqs.map((faq, index) => ({
      id: faq.id,
      sort_order: index
    }));

    try {
      await api.patch(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/faqs/reorder`, {
        faqs: updates
      });
    } catch (error) {
      console.error('Failed to reorder FAQs:', error);
      // Revert on error
      fetchSectionData();
    }
  };

  // Gallery Management
  const handleGalleryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validImageTypes.includes(file.type)) {
      alert('Please upload an image file (JPG, PNG, GIF)');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploadingGalleryImage(true);
    
    try {
      const formData = new FormData();
      formData.append('image', file);

      const response = await api.post(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setSectionGallery(prev => [...prev, response.data.galleryItem]);
      e.target.value = '';
    } catch (error) {
      console.error('Gallery upload error:', error);
      alert('Failed to upload image: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploadingGalleryImage(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;
    
    try {
      await api.delete(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery/${imageId}`);
      setSectionGallery(prev => prev.filter(image => image.id !== imageId));
    } catch (error) {
      console.error('Failed to delete gallery image:', error);
      alert('Failed to delete image: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdateGalleryImage = async (imageId, data) => {
    try {
      await api.put(`/admin/campaigns/${campaignId}/campaign-sections/${sectionId}/gallery/${imageId}`, data);
      setSectionGallery(prev => prev.map(image => 
        image.id === imageId ? { ...image, ...data } : image
      ));
    } catch (error) {
      console.error('Failed to update gallery image:', error);
      alert('Failed to update image');
    }
  };

  if (loading) {
    return (
      <AdminLayout activeSection="CAMPAIGN">
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-500">Loading section...</div>
        </div>
      </AdminLayout>
    );
  }

  if (!section) {
    return (
      <AdminLayout activeSection="CAMPAIGN">
        <div className="flex items-center justify-center h-96">
          <div className="text-xl text-gray-500">Section not found</div>
        </div>
      </AdminLayout>
    );
  }

  const sectionTypes = {
    banner: { title: 'Banner', icon: Target, color: 'bg-blue-100 text-blue-800' },
    content: { title: 'Content', icon: FileText, color: 'bg-purple-100 text-purple-800' },
    forms: { title: 'Form', icon: ClipboardList, color: 'bg-green-100 text-green-800' },
    faqs: { title: 'FAQs', icon: CircleQuestionMark, color: 'bg-yellow-100 text-yellow-800' },
    gallery: { title: 'Gallery', icon: Images, color: 'bg-pink-100 text-pink-800' }
  };

  const sectionInfo = sectionTypes[section.type] || sectionTypes.content;
  const SectionIcon = sectionInfo.icon;

  return (
    <AdminLayout activeSection="CAMPAIGN">
      {/* Header with back button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/admin/campaigns/${campaignId}/edit?tab=theme`)}
            className="text-white hover:text-gray-200 mr-4 flex items-center"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Campaign
          </button>
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${sectionInfo.color}`}>
              <SectionIcon className="w-3.5 h-3.5" />
              {sectionInfo.title}
            </span>
            <h1 className="text-2xl font-bold text-white">
              {section.about_header || section.faqs_header || `${sectionInfo.title} Section`}
            </h1>
          </div>
        </div>
        
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-3 rounded-xl font-medium flex items-center space-x-2 ${
            saving 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-teal-700 hover:bg-teal-600 cursor-pointer'
          } text-white`}
        >
          {saving && (
            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span>{saving ? 'Saving...' : 'Save Section'}</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="bg-white rounded-3xl shadow-2xl p-8 space-y-8">
        
        {/* Background Settings */}
        {(section.type === 'banner' || section.type === 'content') && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">Background Settings</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Background Image/Video</label>
              {section.background_image_url && (
                <div className="mb-4">
                  {section.background_image_url.match(/\\.(mp4|webm|ogg)$/i) ? (
                    <video 
                      src={`${ASSET_BASE_URL}${section.background_image_url}`} 
                      className="h-48 w-full object-cover rounded-lg"
                      controls
                      muted
                    />
                  ) : (
                    <img 
                      src={`${ASSET_BASE_URL}${section.background_image_url}`} 
                      alt="Background" 
                      className="h-48 w-full object-cover rounded-lg"
                    />
                  )}
                </div>
              )}
              <div className="flex items-center space-x-4">
                <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                  uploadingBackground ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300'
                } text-gray-700`}>
                  <span>{uploadingBackground ? 'Uploading...' : 'Upload Background'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/jpeg,image/jpg,image/png,image/gif,video/mp4,video/webm,video/ogg"
                    onChange={handleBackgroundUpload}
                    disabled={uploadingBackground}
                  />
                </label>
                {section.background_image_url && (
                  <button
                    type="button"
                    onClick={() => handleSectionUpdate('background_image_url', '')}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="full_width_background"
                checked={section.full_width_background}
                onChange={(e) => handleSectionUpdate('full_width_background', e.target.checked)}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor="full_width_background" className="ml-2 text-sm text-gray-700">
                Full width background
              </label>
            </div>
          </div>
        )}

        {/* Content Settings */}
        {(section.type === 'content' || section.type === 'banner') && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">Content Settings</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Header</label>
              <input
                type="text"
                value={section.about_header || ''}
                onChange={(e) => handleSectionUpdate('about_header', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                placeholder="Enter section header..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Section Description</label>
              <WysiwygEditor
                value={section.about_description || ''}
                onChange={(html) => handleSectionUpdate('about_description', html)}
                placeholder="Enter section description..."
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="about_with_form"
                checked={section.about_with_form}
                onChange={(e) => handleSectionUpdate('about_with_form', e.target.checked)}
                className="h-4 w-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <label htmlFor="about_with_form" className="ml-2 text-sm text-gray-700">
                Include entry form in this section
              </label>
            </div>
          </div>
        )}

        {/* FAQ Settings */}
        {section.type === 'faqs' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">FAQ Settings</h3>
              <button
                onClick={handleAddFaq}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2"
              >
                <Plus className="h-4 w-4" />
                <span>Add FAQ</span>
              </button>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">FAQ Section Header</label>
              <input
                type="text"
                value={section.faqs_header || ''}
                onChange={(e) => handleSectionUpdate('faqs_header', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg"
                placeholder="Enter FAQ section header..."
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">FAQ Section Description</label>
              <WysiwygEditor
                value={section.faqs_description || ''}
                onChange={(html) => handleSectionUpdate('faqs_description', html)}
                placeholder="Enter FAQ section description..."
              />
            </div>

            {/* FAQ List */}
            <div>
              <h4 className="text-lg font-medium text-gray-700 mb-4">FAQ Questions</h4>
              {sectionFaqs.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleReorderFaqs}
                >
                  <SortableContext
                    items={sectionFaqs.map(faq => faq.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-4">
                      {sectionFaqs.map((faq, index) => (
                        <SortableFaqItem
                          key={faq.id}
                          faq={faq}
                          index={index}
                          onUpdate={handleUpdateFaq}
                          onDelete={handleDeleteFaq}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No FAQs added yet. Click "Add FAQ" to get started.
                </div>
              )}

              {/* Bottom Add FAQ button for easy access after scrolling */}
              {sectionFaqs.length > 0 && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleAddFaq}
                    className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center space-x-2 shadow-lg"
                  >
                    <Plus className="h-5 w-5" />
                    <span>Add Another FAQ</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Gallery Settings */}
        {section.type === 'gallery' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Gallery Settings</h3>
            </div>
            
            {/* Upload New Image */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-600 mb-3">Add New Image</h4>
              <div className="flex items-center space-x-4">
                <label className={`cursor-pointer px-4 py-2 rounded-lg font-medium ${
                  uploadingGalleryImage ? 'bg-gray-300 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 cursor-pointer'
                } text-white`}>
                  <span>{uploadingGalleryImage ? 'Uploading...' : 'Upload Image'}</span>
                  <input
                    type="file"
                    className="sr-only"
                    accept="image/jpeg,image/jpg,image/png,image/gif"
                    onChange={handleGalleryImageUpload}
                    disabled={uploadingGalleryImage}
                  />
                </label>
                <p className="text-xs text-gray-500">
                  Supported formats: JPG, PNG, GIF (max 10MB)
                </p>
              </div>
            </div>

            {/* Gallery Images */}
            <div>
              <h4 className="text-lg font-medium text-gray-700 mb-4">Gallery Images</h4>
              {sectionGallery.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {sectionGallery.map((image, index) => (
                    <div key={image.id} className="relative group">
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={`${ASSET_BASE_URL}${image.image_url}`}
                          alt={image.title || `Gallery image ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Image overlay with controls */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
                          <button
                            type="button"
                            onClick={() => handleDeleteGalleryImage(image.id)}
                            className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            title="Delete image"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Image details form */}
                      <div className="mt-2 space-y-2">
                        <input
                          type="text"
                          value={image.title || ''}
                          onChange={(e) => handleUpdateGalleryImage(image.id, { title: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                          placeholder="Image title..."
                        />
                        <textarea
                          value={image.description || ''}
                          onChange={(e) => handleUpdateGalleryImage(image.id, { description: e.target.value })}
                          className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                          rows={2}
                          placeholder="Image description..."
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No images uploaded yet. Upload your first image above.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Form Settings */}
        {section.type === 'forms' && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">Form Settings</h3>
            <div className="text-center py-8 text-gray-500">
              Form configuration will be handled in the main Forms tab.
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default SectionEdit;